/* eslint-disable react/require-default-props */
import React from 'react';
import {FlatList} from 'react-native';
import {get, isEmpty, noop} from 'lodash';

import LoadingIndicator from '/component/base/LoadingIndicator';
import {DownlineProfile} from '/constant/TestIDs';
import {
  DAY_ONE_CHECK_IN,
  DELIVERED,
  FIRST_WEEK_CELEBRATION,
  JOURNEY_KICK_OFF_CALL,
} from '/enum/clientJourneyTasks.enum';
import {useTranslation} from '/provider/TranslationProvider';

import {TaskItem} from '../../TaskItem/TaskItem';
import {TaskItemDescriptionComponent} from '../../TaskItem/TaskItemDescriptionComponent';

import {useFirstWeekTasks} from './FirstWeekTasks.hook';
import {styles} from './FirstWeekTasks.style';
interface CheckInItem {
  label?: string;
  id?: number;
  checkStatus?: boolean;
  chkBoxCode?: string;
}
interface FirstWeekTasksProps {
  firstWeekInitialTasks?: CheckInItem[];
  firstWeekTasks?: CheckInItem[];
  clientId?: number | null;
  firstOrderShippingStatus?: string | null;
  onShowTaskDetail?: () => void;
  clientJourneyTimer?: number;
  fetchClientJourneyTask?: () => void;
  setClientJourneyTasks?: () => void;
  hasFirstWeekTasks?: boolean;
  setClientJourneyState?: () => void;
}

/**
 * @param {object} params - Parameters
 * @param {Object[]} params.filteredTasks - an object that contains tile data (description)
 * @param {string}   params.filteredTasks.tabDescription
 * @param {string}   params.filteredTasks.screenTabCode
 * @param {Object[]} params.filteredTasks.checkBox
 * @param {string}   params.filteredTasks.checkBox.screenTabCode
 * @param {string}   params.filteredTasks.checkBox.chkBoxCode
 * @param {string}   params.filteredTasks.checkBox.description
 * @param {Object[]} params.firstWeekTasks - an object that contains data about the status of the checkbox
 * @param {number}   params.firstWeekTasks.clientId
 * @param {string}   params.firstWeekTasks.screenTabCode
 * @param {Object[]} params.firstWeekTasks.checkIns
 * @param {string}   params.firstWeekTasks.checkIns.chkBoxCode
 * @param {boolean}  params.firstWeekTasks.checkIns.checkStatus
 * @param {function} params.fetchClientJourneyTask - function for sending a post request to change the status of the checkbox
 * @param {function} params.setClientJourneyTasks - function for changing the status in the redux
 * @returns {function} getTaskItemIcon - return name of icon for tile: 'checkbox-selected' or 'checkbox-unselected'

 */

export const FirstWeekTasks: React.FC<FirstWeekTasksProps> = ({
  firstWeekInitialTasks = [],
  firstWeekTasks = [],
  clientId = null,
  firstOrderShippingStatus = null,
  onShowTaskDetail = noop,
  clientJourneyTimer = 6000,
  fetchClientJourneyTask = noop,
  setClientJourneyTasks = noop,
  hasFirstWeekTasks = false,
  setClientJourneyState = noop,
}) => {
  const {
    firstWeekCompleteData,
    tasks,
    onFirstWeekCompleteChange,
    onTaskStatusChange,
  } = useFirstWeekTasks({
    firstWeekInitialTasks,
    firstWeekTasks,
    clientId,
    clientJourneyTimer,
    fetchClientJourneyTask,
    setClientJourneyTasks,
    setClientJourneyState,
  });
  const {ts} = useTranslation('screen.clients.section.clientJourney.firstWeek');
  const getTaskItemIcon = (taskName: any, taskStatus: any) => {
    if (
      taskName !== JOURNEY_KICK_OFF_CALL &&
      firstOrderShippingStatus !== DELIVERED
    ) {
      return 'checkbox-disabled';
    } else if (taskStatus === true) {
      return 'checkbox-selected';
    } else {
      return 'checkbox-unselected';
    }
  };

  const getFirstWeekCompleteTaskStatus =
    // @ts-expect-error TS(2339): Property 'checkStatus' does not exist on type '{}'... Remove this comment to see the full error message
    firstWeekCompleteData.checkStatus === true ||
    // @ts-expect-error TS(2339): Property 'checkStatus' does not exist on type 'nev... Remove this comment to see the full error message
    (!isEmpty(tasks) && tasks.every(item => item?.checkStatus === true));

  const isTaskDisabled = (taskName: any) =>
    firstOrderShippingStatus !== DELIVERED &&
    taskName !== JOURNEY_KICK_OFF_CALL;

  // @ts-expect-error TS(7030): Not all code paths return a value.
  const renderTaskAvailabilityDescription = (taskName: any) => {
    if (
      taskName === DAY_ONE_CHECK_IN &&
      firstOrderShippingStatus !== DELIVERED
    ) {
      return (
        <TaskItemDescriptionComponent
          description={ts(
            'checkboxAvailabilityText',
            'Available after first order arrives:',
          )}
          style={styles.checkboxStatusText}
          testID={DownlineProfile.cjFirstWeekNoOrderLabel}
        />
      );
    }
  };
  // @ts-expect-error TS(7030): Not all code paths return a value.
  const renderJourneyKickOffCallDescComponent = (
    taskName: any,
    checkStatus: any,
  ) => {
    const itemStyle = checkStatus
      ? {...styles.journeyKickOffCallText, ...styles.selectedText}
      : {...styles.journeyKickOffCallText, ...styles.unSelectedText};
    if (!isEmpty(tasks) && taskName === JOURNEY_KICK_OFF_CALL) {
      return (
        <TaskItemDescriptionComponent
          description={ts(
            'journeyKickOffCallText',
            'Schedule a Journey Kickoff Call the day before this Client begins.',
          )}
          style={itemStyle}
          testID={DownlineProfile.cjTaskKickOfCallDescription}
        />
      );
    }
  };

  // @ts-expect-error TS(7030): Not all code paths return a value.
  const renderFirstWeekCelebrationDescComponent = (
    taskName: any,
    checkStatus: any,
  ) => {
    const itemStyle = checkStatus
      ? {...styles.journeyKickOffCallText, ...styles.selectedText}
      : {...styles.journeyKickOffCallText, ...styles.unSelectedText};
    if (!isEmpty(tasks) && taskName === FIRST_WEEK_CELEBRATION) {
      return (
        <TaskItemDescriptionComponent
          description={ts(
            'firstWeekCelebrationText',
            ' After the first week, check in and schedule the Week 1 Celebration Call with your Training Coach or Support Team member.',
          )}
          style={itemStyle}
          testID={DownlineProfile.cjTaskCelebrationDescription}
        />
      );
    }
  };

  // @ts-expect-error TS(7030): Not all code paths return a value.
  const renderItem = (item: any) => {
    const label = get(item, 'item.label', '');
    const checkStatus = item?.item?.checkStatus;
    if (!isEmpty(tasks)) {
      return (
        <TaskItem
          taskItem={item}
          onTaskStatusChange={onTaskStatusChange}
          iconName={getTaskItemIcon(label, checkStatus)}
          isChecked={checkStatus}
          isDisabled={isTaskDisabled(label)}
          taskAvailabilityTextComponent={renderTaskAvailabilityDescription(
            label,
          )}
          journeyKickOffCallDescComponent={renderJourneyKickOffCallDescComponent(
            label,
            checkStatus,
          )}
          firstWeekCelebrationDescComponent={renderFirstWeekCelebrationDescComponent(
            label,
            checkStatus,
          )}
          onShowTaskDetail={onShowTaskDetail}
          firstOrderShippingStatus={firstOrderShippingStatus}
        />
      );
    }
  };

  // @ts-expect-error TS(7030): Not all code paths return a value.
  const renderHeader = () => {
    if (
      firstOrderShippingStatus &&
      firstOrderShippingStatus === DELIVERED &&
      !isEmpty(tasks)
    ) {
      return (
        <TaskItem
          taskItem={{item: firstWeekCompleteData}}
          onTaskStatusChange={onFirstWeekCompleteChange}
          iconName={
            // @ts-expect-error TS(2339): Property 'checkStatus' does not exist on type '{}'... Remove this comment to see the full error message
            firstWeekCompleteData?.checkStatus === true
              ? 'checkbox-selected'
              : 'checkbox-unselected'
          }
          isChecked={getFirstWeekCompleteTaskStatus}
          isDisabled={false}
          style={{
            taskItem: styles.firstWeekCompleteItem,
          }}
          firstWeekCompleteItem
        />
      );
    }
  };

  // Preparing data for display
  if (isEmpty(tasks) && !hasFirstWeekTasks) {
    // @ts-expect-error TS(2339): Property 'loader' does not exist on type 'NamedSty... Remove this comment to see the full error message
    return <LoadingIndicator size="small" style={styles.loader} />;
  }

  return (
    <FlatList
      scrollEnabled={false}
      style={styles.taskListContainer}
      data={tasks}
      // @ts-expect-error TS(2339): Property 'id' does not exist on type 'never'.
      keyExtractor={item => item.id}
      // @ts-expect-error TS(2769): No overload matches this call.
      renderItem={item => item && renderItem(item)}
      ListHeaderComponent={renderHeader()}
    />
  );
};
